import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { ShieldCheck, Fingerprint, Mail, Link as LinkIcon, BellRing, KeyRound, Sparkles, Loader2, BellOff, Bell } from "lucide-react";
import { puvioService } from "../genesis/identity/puvioService";
import { notificationService } from "../genesis/notifications/notificationService";
import { pushNotifications } from "../genesis/notifications/pushNotifications";
import { toast } from "sonner";

export default function SecuritySheet({ open, onClose, onOpenDevices, onOpenRecovery }) {
  const [me, setMe] = useState(null);
  const [providers, setProviders] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [pushState, setPushState] = useState({ supported: false, permission: "default", subscribed: false });
  const [pushBusy, setPushBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, p, n, ps] = await Promise.all([
        puvioService.me(),
        puvioService.listProviders(),
        notificationService.getPreferences(),
        pushNotifications.status(),
      ]);
      setMe(u); setProviders(p || []); setPrefs(n || null); setPushState(ps);
    } catch (e) {
      toast.error(e?.message || "Couldn't load security info");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const togglePref = async (key, val) => {
    setSaving(key);
    try {
      const p = await notificationService.updatePreferences({ [key]: val });
      setPrefs(p);
    } catch (e) { toast.error(e?.message || "Couldn't save preference"); }
    finally { setSaving(null); }
  };

  const togglePush = async (next) => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (next) {
        await pushNotifications.requestAndSubscribe();
        toast.success("Push notifications enabled.");
      } else {
        await pushNotifications.unsubscribe();
        toast.success("Push notifications turned off.");
      }
      const fresh = await pushNotifications.status();
      setPushState(fresh);
    } catch (e) {
      toast.error(e?.message || "Couldn't change push notifications");
      const fresh = await pushNotifications.status();
      setPushState(fresh);
    } finally { setPushBusy(false); }
  };

  const linkLevel = me?.ecosystem_identity?.verification_status?.level || "unverified";
  const puvioId = me?.puvio_user_id || "—";

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <SheetContent side="bottom" className="rounded-t-[28px] border-0 bg-card max-h-[88vh] overflow-y-auto" data-testid="security-sheet">
        <SheetHeader className="text-left pb-2">
          <div className="inline-flex w-fit items-center gap-1.5 bg-primary/10 text-primary rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider mb-2">
            <ShieldCheck size={11} /> Security & identity
          </div>
          <SheetTitle className="text-[26px] font-extrabold tracking-tight">Security settings</SheetTitle>
          <SheetDescription className="text-[13px] text-muted-foreground font-medium">
            Your Puvio ID is the gateway to the ecosystem. Manage it here.
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 size={18} className="animate-spin" />
          </div>
        )}

        {!loading && me && (
          <div className="mt-3 space-y-5" data-testid="security-content">
            {/* Puvio ID card */}
            <div className="rounded-2xl bg-secondary text-secondary-foreground p-4 relative overflow-hidden" data-testid="security-puvio-card">
              <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-primary/30 blur-2xl" />
              <div className="relative">
                <p className="text-[10px] uppercase tracking-wider font-extrabold opacity-80 flex items-center gap-1.5">
                  <Fingerprint size={11} /> Puvio ID
                </p>
                <p className="font-extrabold text-[15px] mt-1 break-all" data-testid="security-puvio-id">{puvioId}</p>
                <div className="mt-2 inline-flex items-center gap-1.5 bg-white/10 backdrop-blur rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider">
                  <Sparkles size={10} /> {linkLevel.replace("_", " ")}
                </div>
              </div>
            </div>

            {/* Linked providers */}
            <section data-testid="security-providers-section">
              <p className="text-[10px] uppercase tracking-wider font-extrabold text-muted-foreground">Linked sign-in methods</p>
              <div className="mt-2 space-y-2">
                {providers.length === 0 && <p className="text-[12px] text-muted-foreground">No providers linked yet.</p>}
                {providers.map((p) => (
                  <div key={`${p.provider}-${p.subject_id}`} className="rounded-2xl border border-border bg-card p-3 flex items-center gap-3" data-testid={`security-provider-${p.provider}`}>
                    <span className="h-9 w-9 rounded-xl bg-muted flex items-center justify-center text-foreground/70">
                      {p.provider === "google" ? <Mail size={14} /> : p.provider === "guest" ? <KeyRound size={14} /> : <LinkIcon size={14} />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-bold capitalize">{p.provider}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{p.subject_id}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Actions */}
            <section className="space-y-2" data-testid="security-actions">
              <Button variant="outline" className="w-full justify-between h-12 rounded-2xl border-border" onClick={onOpenDevices} data-testid="security-open-devices">
                <span className="flex items-center gap-2 font-bold"><ShieldCheck size={14} /> Manage devices</span>
                <span className="text-[11px] text-muted-foreground">View · revoke</span>
              </Button>
              <Button variant="outline" className="w-full justify-between h-12 rounded-2xl border-border" onClick={onOpenRecovery} data-testid="security-open-recovery">
                <span className="flex items-center gap-2 font-bold"><KeyRound size={14} /> Account recovery</span>
                <span className="text-[11px] text-muted-foreground">Start now</span>
              </Button>
            </section>

            {/* Notification preferences (Genesis notification layer) */}
            {prefs && (
              <section data-testid="security-notifications-section">
                <p className="text-[10px] uppercase tracking-wider font-extrabold text-muted-foreground flex items-center gap-1.5">
                  <BellRing size={11} /> Ecosystem notifications
                </p>

                {/* Push notifications subscription toggle (device-level) */}
                <div className="mt-2 rounded-2xl border border-border bg-card p-3.5" data-testid="security-push-card">
                  <div className="flex items-center gap-3">
                    <span className={`h-9 w-9 rounded-xl flex items-center justify-center ${pushState.subscribed ? "bg-primary/10 text-primary" : "bg-muted text-foreground/60"}`}>
                      {pushState.subscribed ? <Bell size={15} /> : <BellOff size={15} />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-bold leading-tight">Push notifications</p>
                      <p className="text-[11px] text-muted-foreground font-medium mt-0.5 leading-snug">
                        {!pushState.supported
                          ? "This device doesn't support push."
                          : pushState.permission === "denied"
                          ? "Blocked in browser settings · re-enable there to turn on."
                          : pushState.subscribed
                          ? "On for this device · gentle nudges only."
                          : "Off · turn on to receive cycle, reflection & safety alerts on this device."}
                      </p>
                    </div>
                    <Switch
                      checked={!!pushState.subscribed}
                      onCheckedChange={togglePush}
                      disabled={pushBusy || !pushState.supported || pushState.permission === "denied"}
                      data-testid="security-push-toggle"
                    />
                  </div>
                </div>

                <p className="mt-3 text-[10px] uppercase tracking-wider font-extrabold text-muted-foreground">Topics</p>
                <div className="mt-2 rounded-2xl border border-border bg-card divide-y divide-border">
                  {[
                    ["daily_insights", "Daily insights"],
                    ["cycle_reminders", "Cycle reminders"],
                    ["reflection_nudges", "Reflection nudges"],
                    ["safety_alerts", "Safety alerts"],
                  ].map(([k, label]) => (
                    <div key={k} className="flex items-center justify-between px-3.5 py-2.5" data-testid={`security-pref-${k}`}>
                      <Label htmlFor={`pref-${k}`} className="text-[13px] font-medium">{label}</Label>
                      <Switch
                        id={`pref-${k}`}
                        checked={!!prefs[k]}
                        onCheckedChange={(v) => togglePref(k, v)}
                        disabled={saving === k}
                      />
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
